import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IconRefresh, IconX } from '@tabler/icons-react';
import type { BoardStage } from '../../../../entities/board';
import type { Group } from '../../../../entities/group';
import { fetchSourceOptions } from '../../../../entities/lead';
import { fetchPartnersOptions, fetchPartnersByIds } from '../../../../entities/partner';
import type { User } from '../../../../entities/user';
import { pb } from '../../../../shared/api/pocketbase';
import { Button } from '../../../../shared/ui/Button';
import { Select, type SelectOption } from '../../../../shared/ui/Select';
import { Tabs } from '../../../../shared/ui/Tabs';
import { AnalyticsCardsModal } from '../../../../features/analytics-cards-modal';

const PERIOD_OPTIONS: SelectOption[] = [
  { value: 'today',     label: 'Сегодня' },
  { value: 'yesterday', label: 'Вчера' },
  { value: '7d',        label: '7 дней' },
  { value: '30d',       label: '30 дней' },
  { value: 'all',       label: 'Всё время' },
  { value: 'custom',    label: 'Произвольный период' },
];

type AnalyticsPeriod = 'today' | 'yesterday' | '7d' | '30d' | 'all' | 'custom';

interface AnalyticsStageStat {
  stageId: string;
  stageName: string;
  count: number;
  colors?: BoardStage['colors'];
  finishSuccess?: boolean;
  finishClose?: boolean;
}

interface DailyRow {
  date: string;
  // ids карточек по этапу (бекенд возвращает id, не количество)
  counts: Record<string, string[]>;
  total: number;
}

type DailyView = 'week' | 'day';

const DAILY_VIEW_TABS = [
  { value: 'week' as DailyView, label: 'По неделям' },
  { value: 'day' as DailyView, label: 'По дням' },
];

// key от бекенда: день — "YYYY-MM-DD", неделя — "YYYY-WW" (strftime %Y-%W)
const rowDateLabel = (key: string, view: DailyView): string => {
  if (view === 'day') return dayjs(key).format('DD.MM.YYYY');
  const [year, week] = key.split('-').map(Number);
  const jan1 = dayjs(`${year}-01-01`);
  const firstMonday = jan1.add((1 - jan1.day() + 7) % 7, 'day');
  const monday = firstMonday.add((week - 1) * 7, 'day');
  return `${monday.format('DD.MM')}–${monday.add(6, 'day').format('DD.MM.YYYY')}`;
};

const API_BASE = import.meta.env.DEV ? 'http://127.0.0.1:8090' : '';

const FILTERS_KEY = 'analytics_filters';

const readSavedFilters = (): Record<string, unknown> => {
  try { return JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}'); } catch { return {}; }
};

const DEFAULT_FILTERS = {
  period: '30d' as AnalyticsPeriod,
  customStart: '',
  customEnd: '',
  employees: [] as string[],
  sources: [] as string[],
  stageIds: [] as string[],
  partnerIds: [] as string[],
};

const AnalyticsSection: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);

  const [analyticsPeriod, setAnalyticsPeriod] = useState<AnalyticsPeriod>(() => (readSavedFilters().period as AnalyticsPeriod) ?? DEFAULT_FILTERS.period);
  const [analyticsCustomStart, setAnalyticsCustomStart] = useState(() => (readSavedFilters().customStart as string) ?? DEFAULT_FILTERS.customStart);
  const [analyticsCustomEnd, setAnalyticsCustomEnd] = useState(() => (readSavedFilters().customEnd as string) ?? DEFAULT_FILTERS.customEnd);
  const [analyticsEmployees, setAnalyticsEmployees] = useState<string[]>(() => (readSavedFilters().employees as string[]) ?? DEFAULT_FILTERS.employees);
  const [analyticsSources, setAnalyticsSources] = useState<string[]>(() => (readSavedFilters().sources as string[]) ?? DEFAULT_FILTERS.sources);
  const [analyticsPartnerIds, setAnalyticsPartnerIds] = useState<string[]>(() => (readSavedFilters().partnerIds as string[]) ?? DEFAULT_FILTERS.partnerIds);

  const [stages, setStages] = useState<BoardStage[]>([]);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsStageStat[]>([]);
  const [dailyData, setDailyData] = useState<DailyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedStageIds, setSelectedStageIds] = useState<string[]>(() => (readSavedFilters().stageIds as string[]) ?? DEFAULT_FILTERS.stageIds);
  const [dailyView, setDailyView] = useState<DailyView>('week');
  const [cardsModal, setCardsModal] = useState<{ title: string; ids: string[] } | null>(null);

  useEffect(() => {
    pb.collection('users')
      .getFullList<User>({ sort: 'name', fields: 'id,name,email,role,created' })
      .then(setUsers)
      .catch(() => setUsers([]));
    pb.collection('groups')
      .getFullList<Group>({ sort: '-created', expand: 'managers', fields: 'id,name,managers,usersIds,created,updated,expand' })
      .then(setGroups)
      .catch(() => setGroups([]));
  }, []);

  useEffect(() => {
    localStorage.setItem(FILTERS_KEY, JSON.stringify({
      period: analyticsPeriod,
      customStart: analyticsCustomStart,
      customEnd: analyticsCustomEnd,
      employees: analyticsEmployees,
      sources: analyticsSources,
      stageIds: selectedStageIds,
      partnerIds: analyticsPartnerIds,
    }));
  }, [analyticsPeriod, analyticsCustomStart, analyticsCustomEnd, analyticsEmployees, analyticsSources, selectedStageIds, analyticsPartnerIds]);

  const resetFilters = () => {
    localStorage.removeItem(FILTERS_KEY);
    setAnalyticsPeriod(DEFAULT_FILTERS.period);
    setAnalyticsCustomStart(DEFAULT_FILTERS.customStart);
    setAnalyticsCustomEnd(DEFAULT_FILTERS.customEnd);
    setAnalyticsEmployees(DEFAULT_FILTERS.employees);
    setAnalyticsSources(DEFAULT_FILTERS.sources);
    setSelectedStageIds(DEFAULT_FILTERS.stageIds);
    setAnalyticsPartnerIds(DEFAULT_FILTERS.partnerIds);
  };

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const activeStages = await pb.collection('board').getFullList<BoardStage>({
        sort: 'order', filter: 'actual=true', fields: 'id,name,order,actual,colors,finishSuccess,finishClose',
      });
      setStages(activeStages);

      // Build date params
      let startDate: string;
      let endDate = dayjs().format('YYYY-MM-DD');

      if (analyticsPeriod === 'today') {
        startDate = dayjs().format('YYYY-MM-DD');
      } else if (analyticsPeriod === 'yesterday') {
        startDate = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
        endDate = startDate;
      } else if (analyticsPeriod === '7d') {
        startDate = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
      } else if (analyticsPeriod === '30d') {
        startDate = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
      } else if (analyticsPeriod === 'custom') {
        startDate = analyticsCustomStart || '2000-01-01';
        endDate = analyticsCustomEnd || dayjs().format('YYYY-MM-DD');
      } else {
        startDate = '2000-01-01';
      }

      // Build managerId param
      const idSet = new Set<string>();
      for (const sel of analyticsEmployees) {
        if (sel.startsWith('user:')) {
          idSet.add(sel.slice(5));
        } else if (sel.startsWith('group:')) {
          const grp = groups.find(g => g.id === sel.slice(6));
          grp?.usersIds?.forEach(id => idSet.add(id));
        }
      }
      const managerIds = [...idSet].join(',');

      const params = new URLSearchParams({ startDate, endDate });
      if (managerIds) params.set('managerId', managerIds);
      if (analyticsSources.length > 0) params.set('source', analyticsSources.join(','));
      if (analyticsPartnerIds.length > 0) params.set('partnerId', analyticsPartnerIds.join(','));

      const authHeader = { Authorization: pb.authStore.token ? `Bearer ${pb.authStore.token}` : '' };

      const dailyParams = new URLSearchParams(params);
      dailyParams.set('period', dailyView);

      const [statRes, dailyRes] = await Promise.all([
        fetch(`${API_BASE}/api/funnel-stats?${params}`, { headers: authHeader }),
        fetch(`${API_BASE}/api/funnel-stats/daily?${dailyParams}`, { headers: authHeader }),
      ]);

      const statData: Record<string, number> = await statRes.json();
      const dailyRaw: Record<string, Record<string, string[]>> = await dailyRes.json();

      setAnalyticsData(activeStages.map(s => ({
        stageId: s.id,
        stageName: s.name,
        count: statData[s.id] || 0,
        colors: s.colors,
        finishSuccess: s.finishSuccess,
        finishClose: s.finishClose,
      })));

      setDailyData(
        Object.entries(dailyRaw)
          .map(([date, counts]) => ({
            date,
            counts,
            total: Object.values(counts).reduce((s, ids) => s + ids.length, 0),
          }))
          .filter(r => r.total > 0)
          .sort((a, b) => b.date.localeCompare(a.date)),
      );
    } catch {
      setAnalyticsData([]);
      setDailyData([]);
    } finally {
      setLoading(false);
    }
  }, [analyticsPeriod, analyticsCustomStart, analyticsCustomEnd, analyticsEmployees, analyticsSources, analyticsPartnerIds, groups, dailyView]);

  useEffect(() => {
    void loadAnalytics();
  }, [loadAnalytics]);


  const employeeOptions = useMemo((): SelectOption[] => {
    const opts: SelectOption[] = [];
    groups.forEach(g => {
      const label = g.name || `Группа ${g.id.slice(-4)}`;
      opts.push({ value: `group:${g.id}`, label: `Группа: ${label}` });
    });
    users.filter(u => u.role?.toUpperCase() !== 'ADMIN').forEach(u => {
      opts.push({ value: `user:${u.id}`, label: u.name || u.email });
    });
    return opts;
  }, [users, groups]);

  const visibleStages = useMemo(
    () => selectedStageIds.length === 0 ? stages : stages.filter(s => selectedStageIds.includes(s.id)),
    [stages, selectedStageIds],
  );

  const visibleAnalyticsData = useMemo(
    () => selectedStageIds.length === 0 ? analyticsData : analyticsData.filter(d => selectedStageIds.includes(d.stageId)),
    [analyticsData, selectedStageIds],
  );

  const funnelData = useMemo(
    () => analyticsData.filter(d => !d.finishClose && (selectedStageIds.length === 0 || selectedStageIds.includes(d.stageId))),
    [analyticsData, selectedStageIds],
  );

  const conversionMatrix = useMemo(() => {
    if (funnelData.length < 2) return null;
    return {
      rows: funnelData.slice(0, -1),
      cols: funnelData.slice(1),
    };
  }, [funnelData]);

  const firstCount = funnelData[0]?.count || 1;
  const finishSuccessStat = analyticsData.find(d => d.finishSuccess);
  const finishCloseStat = analyticsData.find(d => d.finishClose);
  const finishSuccessConv = finishSuccessStat && firstCount > 0
    ? (finishSuccessStat.count / firstCount) * 100
    : null;
  const finishCloseConv = finishCloseStat && firstCount > 0
    ? (finishCloseStat.count / firstCount) * 100
    : null;

  return (
    <div className="adm-section">
      <div className="adm-section-header">
        <h2 className="adm-section-title">Воронка продаж</h2>
        <p className="adm-section-sub">Конверсии по этапам</p>
      </div>

      <div className="adm-analytics-filters">
        <div className="adm-analytics-period">
          <Select
            options={PERIOD_OPTIONS}
            value={analyticsPeriod}
            onChange={v => setAnalyticsPeriod(v as AnalyticsPeriod)}
            placeholder="Период"
          />
        </div>
        {analyticsPeriod === 'custom' && (
          <div className="adm-date-range">
            <input type="date" className="adm-date-input" value={analyticsCustomStart}
              onChange={e => setAnalyticsCustomStart(e.target.value)} />
            <span className="adm-date-sep">—</span>
            <input type="date" className="adm-date-input" value={analyticsCustomEnd}
              onChange={e => setAnalyticsCustomEnd(e.target.value)} />
          </div>
        )}
        <div className="adm-analytics-employee">
          <Select
            mode="multi"
            options={employeeOptions}
            value={analyticsEmployees}
            onChange={setAnalyticsEmployees}
            placeholder="Все сотрудники"
            searchable
          />
        </div>
        <div className="adm-analytics-source">
          <Select
            mode="multi"
            fetchOptions={fetchSourceOptions}
            value={analyticsSources}
            onChange={setAnalyticsSources}
            placeholder="Все источники"
            searchable
          />
        </div>
        <div className="adm-analytics-source">
          <Select
            mode="multi"
            fetchOptions={fetchPartnersOptions}
            fetchByValues={fetchPartnersByIds}
            value={analyticsPartnerIds}
            onChange={setAnalyticsPartnerIds}
            placeholder="Все партнёры"
          />
        </div>
        {stages.length > 0 && (
          <div className="adm-analytics-stages">
            <Select
              mode="multi"
              options={stages.map(s => ({ value: s.id, label: s.name }))}
              value={selectedStageIds}
              onChange={setSelectedStageIds}
              placeholder="Все колонки"
              searchable
            />
          </div>
        )}
        <Button variant="adm-add" type="button" onClick={() => { void loadAnalytics(); }}>
          <IconRefresh size={14} stroke={2} />Обновить
        </Button>
        <Button variant="adm-cancel" type="button" onClick={resetFilters}>
          <IconX size={14} stroke={2} />Сбросить
        </Button>
      </div>

      {loading ? (
        <div className="adm-loading">Загрузка...</div>
      ) : analyticsData.length === 0 ? (
        <div className="adm-empty">Нет данных</div>
      ) : (
        <div className="af-blocks">
          <div className="af-layout">
            {/* Funnel */}
            <div className="af-funnel-card">
              <div className="af-funnel-card-title">Воронка</div>
              <div className="af-funnel">
                {funnelData.map((row, i) => {
                  const widthPct = firstCount > 0 ? Math.min(Math.max((row.count / firstCount) * 100, 8), 100) : 8;
                  const prevCount = i > 0 ? funnelData[i - 1].count : row.count;
                  const convPrev = i > 0 ? (prevCount > 0 ? (row.count / prevCount) * 100 : 0) : null;
                  const totalPct = i === 0 ? 100 : (firstCount > 0 ? (row.count / firstCount) * 100 : 0);
                  const c = row.colors;
                  const bg = c?.dotBg ?? 'var(--orange)';
                  const headerBg = c?.headerBg ?? 'var(--orange-light)';
                  const nameColor = c?.nameColor ?? 'var(--orange-dark)';
                  const connCls = convPrev !== null ? (convPrev < 50 ? ' low' : convPrev < 80 ? ' med' : '') : '';

                  return (
                    <React.Fragment key={row.stageId}>
                      {convPrev !== null && (
                        <div className={`af-funnel-arrow${connCls}`}>
                          <span className="af-funnel-arrow-icon">↓</span>
                          <span className="af-funnel-arrow-badge">{convPrev?.toFixed(1)}%</span>
                        </div>
                      )}
                      <div className="af-funnel-row">
                        <div className="af-funnel-label">
                          <span className="af-dot" style={{ background: bg }} />
                          <span className="af-name" style={{ color: nameColor }}>{row.stageName}</span>
                        </div>
                        <div className="af-funnel-bar-area">
                          <div
                            className="af-funnel-bar"
                            style={{ width: `${widthPct}%`, background: headerBg, borderColor: bg + '40' } as React.CSSProperties}
                          >
                            <span className="af-count" style={{ color: nameColor }}>{row.count.toLocaleString()}</span>
                          </div>
                          <span className="af-funnel-pct">{totalPct.toFixed(1)}%</span>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Right stats */}
            <div className="af-stats">
              {(finishSuccessConv !== null || finishCloseConv !== null) && (
                <div className="af-stat-card af-conv-card">
                  {finishSuccessConv !== null && finishSuccessStat && (
                    <div className="af-conv-block">
                      <div className="af-conv-header">
                        <span className="af-conv-label">Конверсия в успех</span>
                        <span className={`af-conv-badge${finishSuccessConv < 20 ? ' low' : finishSuccessConv < 50 ? ' med' : ' good'}`}>
                          {finishSuccessConv.toFixed(1)}%
                        </span>
                      </div>
                      <div className="af-conv-track">
                        <div
                          className={`af-conv-fill${finishSuccessConv < 20 ? ' low' : finishSuccessConv < 50 ? ' med' : ' good'}`}
                          style={{ width: `${finishSuccessConv}%` }}
                        />
                      </div>
                      <div className="af-conv-sub">{funnelData[0]?.stageName} → {finishSuccessStat.stageName}</div>
                    </div>
                  )}
                  {finishCloseConv !== null && finishCloseStat && (
                    <>
                      {finishSuccessConv !== null && <div className="af-conv-divider" />}
                      <div className="af-conv-block">
                        <div className="af-conv-header">
                          <span className="af-conv-label">Закрытые без сделки</span>
                          <span className="af-conv-badge low">{finishCloseConv.toFixed(1)}%</span>
                        </div>
                        <div className="af-conv-track">
                          <div className="af-conv-fill low" style={{ width: `${finishCloseConv}%` }} />
                        </div>
                        <div className="af-conv-sub">{finishCloseStat.stageName}: {finishCloseStat.count.toLocaleString()}</div>
                      </div>
                    </>
                  )}
                </div>
              )}
              <div className="af-stat-card af-stat-stages">
                <div className="af-stat-label">По этапам</div>
                {visibleAnalyticsData.map((row, i) => {
                  const pct = firstCount > 0 ? (row.count / firstCount) * 100 : 0;
                  const fillPct = i === 0 ? 100 : pct;
                  const c = row.colors;
                  const barColor = c?.dotBg ?? 'var(--orange)';
                  return (
                    <div key={row.stageId} className="af-stat-stage-row">
                      <div className="af-stat-stage-top">
                        <span className="af-stat-stage-dot" style={{ background: barColor }} />
                        <span className="af-stat-stage-name">{row.stageName}</span>
                        <span className="af-stat-stage-count">{row.count.toLocaleString()}</span>
                        <span className="af-stat-stage-pct">{i === 0 ? '100.00%' : `${pct.toFixed(1)}%`}</span>
                      </div>
                      <div className="af-stat-stage-track">
                        <div className="af-stat-stage-fill" style={{ width: `${fillPct}%`, background: barColor }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Conversion matrix */}
          {conversionMatrix && (
            <div className="af-daily">
              <div className="af-daily-title">Матрица конверсий</div>
              <div className="af-daily-wrap">
                <table className="af-daily-table af-matrix-table">
                  <thead>
                    <tr>
                      <th className="af-th af-th-date" style={{ textAlign: 'left' }}>Этап \ Цель</th>
                      {conversionMatrix.cols.map(col => (
                        <th key={col.stageId} className="af-th">
                          {col.stageName}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {conversionMatrix.rows.map((rowStage, rowIdx) => (
                      <tr key={rowStage.stageId} className="af-tr">
                        <td className="af-td af-td-date">
                          {rowStage.stageName}
                          <span className="af-matrix-base">{rowStage.count.toLocaleString()}</span>
                        </td>
                        {conversionMatrix.cols.map((colStage, colIdx) => {
                          if (colIdx < rowIdx) {
                            return <td key={colStage.stageId} className="af-td af-matrix-empty" />;
                          }
                          const pct = rowStage.count > 0
                            ? (colStage.count / rowStage.count) * 100
                            : 0;
                          const isDirect = colIdx === rowIdx;
                          return (
                            <td
                              key={colStage.stageId}
                              className={`af-td af-matrix-cell${isDirect ? ' direct' : ''}`}
                            >
                              {pct.toFixed(1)}%
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Daily table */}
          {dailyData.length > 0 && (
            <div className="af-daily">
              <div className="af-daily-head">
                <div className="af-daily-title">{dailyView === 'week' ? 'По неделям' : 'По дням'}</div>
                <Tabs tabs={DAILY_VIEW_TABS} active={dailyView} onChange={setDailyView} />
              </div>
              <div className="af-daily-wrap">
                <table className="af-daily-table">
                  <thead>
                    <tr>
                      <th className="af-th af-th-date">{dailyView === 'week' ? 'Неделя' : 'Дата'}</th>
                      {visibleStages.map(s => (
                        <th key={s.id} className="af-th">
                          {s.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="af-tr af-tr-total">
                      <td className="af-td af-td-date af-td-total-label">Всего</td>
                      {visibleStages.map(s => {
                        const colTotal = dailyData.reduce((sum, row) => sum + (row.counts[s.id]?.length || 0), 0);
                        return (
                          <td key={s.id} className="af-td af-td-count af-td-total-val">
                            {colTotal}
                          </td>
                        );
                      })}
                    </tr>
                    {dailyData.map(row => {
                      const dateLabel = rowDateLabel(row.date, dailyView);
                      return (
                        <React.Fragment key={row.date}>
                          <tr className="af-tr">
                            <td className="af-td af-td-date">{dateLabel}</td>
                            {visibleStages.map(s => {
                              const ids = row.counts[s.id] || [];
                              const n = ids.length;
                              return (
                                <td
                                  key={s.id}
                                  className={`af-td af-td-count${n > 0 ? ' has-data' : ''}`}
                                  onClick={n > 0 ? () => setCardsModal({ title: `${s.name} · ${dateLabel}`, ids }) : undefined}
                                  style={n > 0 ? { cursor: 'pointer' } : undefined}
                                >
                                  {n}
                                </td>
                              );
                            })}
                          </tr>
                          <tr className="af-tr af-tr-conv">
                            <td className="af-td af-td-date af-td-conv-label">конв.</td>
                            {visibleStages.map((s, j) => {
                              if (j === 0) return <td key={s.id} className="af-td af-td-conv" />;
                              const prev = row.counts[visibleStages[j - 1].id]?.length || 0;
                              const cur = row.counts[s.id]?.length || 0;
                              const pct = prev > 0 ? (cur / prev) * 100 : null;
                              return (
                                <td key={s.id} className="af-td af-td-conv">
                                  {pct === null ? '—' : `${pct.toFixed(0)}%`}
                                </td>
                              );
                            })}
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {cardsModal && (
        <AnalyticsCardsModal
          title={cardsModal.title}
          ids={cardsModal.ids}
          onClose={() => setCardsModal(null)}
        />
      )}
    </div>
  );
};

export { AnalyticsSection };
